import React, { useState, useEffect } from 'react';
import { useStoreSettings } from '../../context/StoreSettingsContext';
import { usePrivacy } from '../../context/PrivacyContext';
import { 
  X, Store, User, FileText, Phone, Mail, MapPin, 
  Upload, Trash2, CheckCircle2, Sparkles, Image, MessageSquare, Eye, Lock, ShieldCheck
} from 'lucide-react';
import { showToast } from '../../lib/toast';

export const StoreSettingsModal: React.FC = () => {
  const { storeSettings, updateStoreSettings, uploadLogo, isSettingsModalOpen, closeSettingsModal, isSaving } = useStoreSettings();
  const { valuesVisible, requireUnlock } = usePrivacy();

  const [formData, setFormData] = useState(storeSettings);
  const [logoPreview, setLogoPreview] = useState<string>(storeSettings.logoUrl || '');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isSettingsModalOpen) {
      setFormData(storeSettings);
      setLogoPreview(storeSettings.logoUrl || '');
    }
  }, [isSettingsModalOpen, storeSettings]);

  if (!isSettingsModalOpen) return null;

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!requireUnlock('alterar o logotipo da loja')) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      showToast('Por favor, selecione um arquivo de imagem válido (PNG, JPG, SVG).', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast('A imagem deve ter no máximo 2MB para garantir alta velocidade.', 'error');
      return;
    }

    setIsUploading(true);
    const res = await uploadLogo(file);
    setIsUploading(false);

    if (res.success && res.url) {
      setLogoPreview(res.url);
      setFormData(prev => ({ ...prev, logoUrl: res.url }));
      showToast('Logo carregado com sucesso!', 'success');
    } else {
      showToast(res.error || 'Erro ao enviar imagem do logo.', 'error');
    }
  };

  const handleRemoveLogo = () => {
    if (!requireUnlock('remover o logotipo da loja')) {
      return;
    }
    setLogoPreview('');
    setFormData(prev => ({ ...prev, logoUrl: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireUnlock('salvar as Configurações da Loja')) {
      return;
    }
    if (!formData.storeName.trim()) {
      showToast('Por favor, informe o Nome da Ótica.', 'error');
      return;
    }
    if (!formData.attendantName.trim()) {
      showToast('Por favor, informe o Nome do Atendente / Responsável.', 'error');
      return;
    }

    await updateStoreSettings(formData);
    showToast('Configurações salvas e sincronizadas com sucesso!', 'success');
    closeSettingsModal();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div
        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-md">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight">Configurações da Ótica & Atendente</h3>
              <p className="text-xs text-blue-100 font-medium">
                Personalize os dados da sua ótica para relatórios PDF, rodapé e mensagens de WhatsApp
              </p>
            </div>
          </div>
          <button
            onClick={closeSettingsModal}
            className="p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6 custom-scrollbar">
          
          {/* Alerta de Modo Valores Ocultos Ativo */}
          {!valuesVisible && (
            <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center justify-between text-xs text-amber-700 dark:text-amber-300">
              <div className="flex items-center gap-2.5">
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 animate-pulse" />
                <div>
                  <p className="font-bold">Modo Valores Ocultos Ativo (Protegido por PIN)</p>
                  <p className="text-[11px] opacity-80">As alterações nas configurações e no PIN estão travadas para operadores.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => requireUnlock('liberar as Configurações da Loja')}
                className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-xs transition-all shrink-0 shadow-xs active:scale-95 flex items-center gap-1.5"
              >
                <Lock className="w-3 h-3" />
                <span>Digitar PIN</span>
              </button>
            </div>
          )}

          {/* Sessão 1: Identificação Principal e Logo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {/* Upload do Logo */}
            <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 text-center">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-1.5">
                <Image className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                Logo da Ótica
              </label>

              <div className="relative group w-28 h-28 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 flex items-center justify-center overflow-hidden shadow-inner">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="text-slate-400 flex flex-col items-center gap-1 p-2">
                    <Store className="w-8 h-8 opacity-40 text-blue-500" />
                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Sem Logo</span>
                  </div>
                )}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <label className="cursor-pointer px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-all shadow-xs flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" />
                  <span>{logoPreview ? 'Trocar' : 'Carregar'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="p-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400 transition-colors"
                    title="Remover Logo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-2 font-medium">Recomendado: PNG ou JPG transparente</p>
            </div>

            {/* Nome da Ótica e Atendente */}
            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Store className="w-4 h-4 text-blue-500" />
                  Nome da Ótica (Empresa) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Óticas Diana, Ótica Visão Real..."
                  value={formData.storeName}
                  onChange={(e) => handleChange('storeName', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <User className="w-4 h-4 text-indigo-500" />
                    Atendente / Responsável *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Diana, Carlos..."
                    value={formData.attendantName}
                    onChange={(e) => handleChange('attendantName', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Aparece nas mensagens do WhatsApp e no rodapé</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-emerald-500" />
                    CNPJ da Loja
                  </label>
                  <input
                    type="text"
                    placeholder="00.000.000/0000-00"
                    value={formData.cnpj}
                    onChange={(e) => handleChange('cnpj', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sessão 2: Contato e Endereço */}
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 space-y-4">
            <h4 className="text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-rose-500" />
              Endereço & Contato (Usado no Cabeçalho do PDF)
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-blue-500" />
                  Telefone / WhatsApp da Ótica
                </label>
                <input
                  type="text"
                  placeholder="(00) 00000-0000"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs font-medium outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-indigo-500" />
                  E-mail Comercial
                </label>
                <input
                  type="email"
                  placeholder="contato@suaotica.com.br"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs font-medium outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Rua / Logradouro</label>
                <input
                  type="text"
                  placeholder="Av. Paulista, Rua das Flores..."
                  value={formData.addressStreet}
                  onChange={(e) => handleChange('addressStreet', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Número</label>
                <input
                  type="text"
                  placeholder="123"
                  value={formData.addressNumber}
                  onChange={(e) => handleChange('addressNumber', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Complemento</label>
                <input
                  type="text"
                  placeholder="Sala 1, Loja B..."
                  value={formData.addressComplement || ''}
                  onChange={(e) => handleChange('addressComplement', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Bairro</label>
                <input
                  type="text"
                  placeholder="Centro"
                  value={formData.addressNeighborhood}
                  onChange={(e) => handleChange('addressNeighborhood', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Cidade</label>
                <input
                  type="text"
                  placeholder="São Paulo"
                  value={formData.addressCity}
                  onChange={(e) => handleChange('addressCity', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Estado (UF)</label>
                <input
                  type="text"
                  maxLength={2}
                  placeholder="SP"
                  value={formData.addressState}
                  onChange={(e) => handleChange('addressState', e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 text-xs uppercase outline-none"
                />
              </div>
            </div>
          </div>

          {/* Sessão: Segurança & PIN do Modo Valores Ocultos */}
          <div className="p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black uppercase tracking-wider text-amber-900 dark:text-amber-300 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Segurança: PIN de 4 Dígitos (Nuvem / Supabase)
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                Sincronizado na Nuvem
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5 text-amber-600" />
                  PIN do Modo Oculto (4 Dígitos)
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="1234"
                  value={formData.privacyPin || ''}
                  onChange={(e) => handleChange('privacyPin', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  disabled={!valuesVisible}
                  className="w-full px-4 py-2.5 rounded-xl border border-amber-300 dark:border-amber-700/80 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 font-mono text-center tracking-widest text-base font-bold outline-none focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                />
              </div>

              <div className="sm:col-span-2 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>
                  Quando o modo <strong>Valores Ocultos</strong> estiver ativado, este PIN é exigido para <strong>desocultar os valores</strong> e destravar a <strong>edição/exclusão de OS, clientes e finanças</strong>.
                </p>
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1 font-semibold">
                  * Apenas quem souber este PIN poderá visualizar valores e editar/apagar registros. A criação de novas OS e clientes continuará sempre liberada.
                </p>
              </div>
            </div>
          </div>

          {/* Sessão 3: Pré-visualização Instantânea do WhatsApp & Rodapé */}
          <div className="p-4 rounded-2xl bg-blue-50/70 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black uppercase tracking-wider text-blue-900 dark:text-blue-300 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                Como seus dados aparecem no sistema
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white">
                50 Mensagens Dinâmicas
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {/* Preview WhatsApp */}
              <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-300 dark:border-slate-700">
                <p className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Exemplo de Mensagem de WhatsApp
                </p>
                <p className="text-slate-600 dark:text-slate-300 italic text-[11px] leading-relaxed">
                  "Olá João! Sou a <strong className="text-blue-600 dark:text-blue-400">{formData.attendantName || 'Diana'}</strong> da <strong className="text-blue-600 dark:text-blue-400">{formData.storeName || 'Óticas Diana'}</strong>! 🎂 Hoje é seu dia especial e queremos desejar um Feliz Aniversário..."
                </p>
              </div>

              {/* Preview Rodapé */}
              <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-300 dark:border-slate-700">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Exibição no Rodapé
                </p>
                <div className="flex items-center gap-2.5 mt-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      (formData.attendantName || 'D').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-slate-900 dark:text-slate-50 truncate">
                      {formData.attendantName || 'Diana'} <span className="text-[10px] text-slate-400 font-normal">• Responsável</span>
                    </p>
                    <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 truncate">
                      {formData.storeName || 'Óticas Diana'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeSettingsModal}
              className="px-5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!valuesVisible || isSaving || isUploading}
              title={!valuesVisible ? 'Desbloqueie com o PIN para salvar' : undefined}
              className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-black shadow-md shadow-blue-600/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
            >
              <CheckCircle2 className={`w-4 h-4 ${isSaving || isUploading ? 'animate-spin' : ''}`} />
              <span>{isSaving ? 'Salvando no Banco...' : isUploading ? 'Enviando Logo...' : !valuesVisible ? '🔒 Bloqueado por PIN' : 'Salvar Configurações'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
